// src/common/ui/citation-type-filter.js
// Citation type filter manager - tracks types found in document and allows filtering

import { formatCitationType } from '../utils/type-formatter.js';

/**
 * All known citation type categories and their subtypes.
 * Pre-populated so users can configure before any parsing.
 */
const CATEGORY_DEFINITIONS = {
  'Precedent': {
    label: 'Precedent (Case Law)',
    defaultVerify: true,
    defaultShow: true,
    subtypes: [
      { key: 'Precedent > US Case law', label: 'US Case law', defaultVerify: true },
      { key: 'Precedent > UK Case law', label: 'UK Case law', defaultVerify: true },
      { key: 'Precedent > Canadian Case law', label: 'Canadian Case law', defaultVerify: true },
      { key: 'Precedent > Australian Case law', label: 'Australian Case law', defaultVerify: true },
      { key: 'Precedent > Public International Law', label: 'Public International Law', defaultVerify: true },
      { key: 'Precedent > Human rights', label: 'Human rights', defaultVerify: true },
      { key: 'Precedent > International Arbitration', label: 'International Arbitration', defaultVerify: true },
      { key: 'Precedent > Sports Arbitration', label: 'Sports Arbitration', defaultVerify: true },
      { key: 'Precedent > Other', label: 'Other', defaultVerify: true },
    ]
  },
  'Legal Norm': {
    label: 'Legal Norms (Statutes)',
    defaultVerify: true,
    defaultShow: true,
    subtypes: [
      { key: 'Legal Norm > US law', label: 'US law', defaultVerify: true },
      { key: 'Legal Norm > UK Law', label: 'UK Law', defaultVerify: true },
      { key: 'Legal Norm > EU Law', label: 'EU Law', defaultVerify: true },
      { key: 'Legal Norm > French Law', label: 'French Law', defaultVerify: true },
      { key: 'Legal Norm > Canadian Law', label: 'Canadian Law', defaultVerify: true },
      { key: 'Legal Norm > International Law', label: 'International Law', defaultVerify: true },
      { key: 'Legal Norm > Other', label: 'Other', defaultVerify: true },
    ]
  },
  'Academic': {
    label: 'Academic Sources',
    defaultVerify: true,
    defaultShow: true,
    subtypes: [
      { key: 'Academic > academic', label: 'Academic', defaultVerify: true },
    ]
  },
  'InCase': {
    label: 'In-Case Materials',
    defaultVerify: false,  // Auto-excluded by default
    defaultShow: false,
    subtypes: [
      { key: 'InCase > legal submission', label: 'Legal submission', defaultVerify: false },
      { key: 'InCase > factual exhibit', label: 'Factual exhibit', defaultVerify: false },
      { key: 'InCase > Other', label: 'Other', defaultVerify: false },
    ]
  },
  'News': {
    label: 'News Sources',
    defaultVerify: true,
    defaultShow: true,
    subtypes: [
      { key: 'News > news', label: 'News', defaultVerify: true },
      { key: 'News > Other', label: 'Other', defaultVerify: true },
    ]
  },
  'Other': {
    label: 'Other Sources',
    defaultVerify: false,  // Most subtypes excluded by default
    defaultShow: true,
    subtypes: [
      { key: 'Other > website', label: 'Website', defaultVerify: false },
      { key: 'Other > UN Docs', label: 'UN Documents', defaultVerify: true },  // Only UN Docs verified
      { key: 'Other > other', label: 'Other', defaultVerify: false },
    ]
  },
};

/**
 * Citation Type Filter Manager
 * Pre-populated with all known categories, tracks counts as citations are parsed
 */
export class CitationTypeFilterManager {
  constructor() {
    // Category-level settings: { show, verify, expanded }
    this.categorySettings = new Map();
    
    // Subtype-level: { count, show, verify }
    this.subtypeSettings = new Map();
    
    this.callbacks = {
      onFilterChange: null,
      onTypesUpdated: null
    };
    this.containerEl = null;
    
    // Initialize with all known categories
    this._initializeCategories();
  }

  /**
   * Initialize all categories and subtypes with default settings
   */
  _initializeCategories() {
    for (const [categoryKey, categoryDef] of Object.entries(CATEGORY_DEFINITIONS)) {
      // Initialize category
      this.categorySettings.set(categoryKey, {
        show: categoryDef.defaultShow,
        verify: categoryDef.defaultVerify,
        expanded: false,  // Collapsed by default
        totalCount: 0
      });
      
      // Initialize subtypes
      for (const subtype of categoryDef.subtypes) {
        this.subtypeSettings.set(subtype.key, {
          count: 0,
          show: categoryDef.defaultShow,
          verify: subtype.defaultVerify
        });
      }
    }
  }

  /**
   * Set callbacks for filter events
   */
  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Reset all filters and counts (e.g., when loading a new document)
   */
  reset() {
    // Reset counts but keep settings
    for (const [, settings] of this.categorySettings) {
      settings.totalCount = 0;
    }
    for (const [, settings] of this.subtypeSettings) {
      settings.count = 0;
    }
    this._renderFilterUI();
  }

  /**
   * Get the normalized type key from a citation's source type
   */
  _getTypeKey(sourceType) {
    return formatCitationType(sourceType);
  }

  /**
   * Get the category for a type key
   */
  _getCategoryForType(typeKey) {
    if (!typeKey || typeKey === '—') return null;
    const parts = typeKey.split(' > ');
    return parts[0] || null;
  }

  /**
   * Register a citation type found in the document
   */
  registerType(sourceType) {
    const typeKey = this._getTypeKey(sourceType);
    if (!typeKey || typeKey === '—') return;

    const categoryKey = this._getCategoryForType(typeKey);
    if (!categoryKey) return;

    // Increment subtype count
    if (this.subtypeSettings.has(typeKey)) {
      const subtype = this.subtypeSettings.get(typeKey);
      subtype.count++;
      this._updateSubtypeCount(typeKey, subtype.count);
    } else {
      // Unknown subtype - add it dynamically
      this.subtypeSettings.set(typeKey, {
        count: 1,
        show: true,
        verify: true
      });
    }

    // Increment category total
    if (this.categorySettings.has(categoryKey)) {
      const category = this.categorySettings.get(categoryKey);
      category.totalCount++;
      this._updateCategoryCount(categoryKey, category.totalCount);
    }

    this.callbacks.onTypesUpdated?.(this.getTypeStats());
  }

  /**
   * Register multiple citations at once
   */
  registerCitations(citations) {
    if (!citations || !Array.isArray(citations)) return;
    
    for (const citation of citations) {
      if (citation?.source?.type) {
        this.registerType(citation.source.type);
      }
      if (citation?.children && Array.isArray(citation.children)) {
        this.registerCitations(citation.children);
      }
    }
  }

  /**
   * Check if a citation should be shown based on its type
   */
  shouldShow(sourceType) {
    const typeKey = this._getTypeKey(sourceType);
    if (!typeKey || typeKey === '—') return true;
    
    const categoryKey = this._getCategoryForType(typeKey);
    
    // Check category-level setting first
    const category = this.categorySettings.get(categoryKey);
    if (category && !category.show) return false;
    
    // Then check subtype setting
    const subtype = this.subtypeSettings.get(typeKey);
    return subtype ? subtype.show : true;
  }

  /**
   * Check if a citation should be verified based on its type
   */
  shouldVerify(sourceType) {
    const typeKey = this._getTypeKey(sourceType);
    if (!typeKey || typeKey === '—') return true;
    
    const categoryKey = this._getCategoryForType(typeKey);
    
    // Check category-level setting first
    const category = this.categorySettings.get(categoryKey);
    if (category && !category.verify) return false;
    
    // Then check subtype setting
    const subtype = this.subtypeSettings.get(typeKey);
    return subtype ? subtype.verify : true;
  }

  /**
   * Get list of hidden type keys
   */
  getHiddenTypes() {
    const hidden = [];
    
    // Add all subtypes from hidden categories
    for (const [categoryKey, settings] of this.categorySettings) {
      if (!settings.show) {
        const categoryDef = CATEGORY_DEFINITIONS[categoryKey];
        if (categoryDef) {
          for (const subtype of categoryDef.subtypes) {
            hidden.push(subtype.key);
          }
        }
      }
    }
    
    // Add individually hidden subtypes
    for (const [typeKey, settings] of this.subtypeSettings) {
      if (!settings.show && !hidden.includes(typeKey)) {
        hidden.push(typeKey);
      }
    }
    
    return hidden;
  }

  /**
   * Get list of excluded type keys (for verification)
   */
  getExcludedVerifyTypes() {
    const excluded = [];
    
    // Add all subtypes from non-verified categories
    for (const [categoryKey, settings] of this.categorySettings) {
      if (!settings.verify) {
        const categoryDef = CATEGORY_DEFINITIONS[categoryKey];
        if (categoryDef) {
          for (const subtype of categoryDef.subtypes) {
            excluded.push(subtype.key);
          }
        }
      }
    }
    
    // Add individually excluded subtypes
    for (const [typeKey, settings] of this.subtypeSettings) {
      if (!settings.verify && !excluded.includes(typeKey)) {
        excluded.push(typeKey);
      }
    }
    
    return excluded;
  }

  /**
   * Get all type statistics
   */
  getTypeStats() {
    let totalCount = 0;
    let visibleCount = 0;
    let verifyCount = 0;
    
    for (const [typeKey, settings] of this.subtypeSettings) {
      totalCount += settings.count;
      
      // Check category-level settings first
      const categoryKey = this._getCategoryForType(typeKey);
      const category = this.categorySettings.get(categoryKey);
      
      // Visible if both category and subtype are shown
      const isVisible = (!category || category.show) && settings.show;
      if (isVisible) visibleCount += settings.count;
      
      // Verify if both category and subtype have verify enabled
      const shouldVerify = (!category || category.verify) && settings.verify;
      if (shouldVerify) verifyCount += settings.count;
    }
    
    return {
      categories: new Map(this.categorySettings),
      subtypes: new Map(this.subtypeSettings),
      totalCount,
      visibleCount,
      verifyCount
    };
  }

  /**
   * Toggle show setting for a category
   */
  setCategoryShow(categoryKey, show) {
    const category = this.categorySettings.get(categoryKey);
    if (category) {
      category.show = show;
      // Also update all subtypes
      const categoryDef = CATEGORY_DEFINITIONS[categoryKey];
      if (categoryDef) {
        for (const subtype of categoryDef.subtypes) {
          const subtypeSettings = this.subtypeSettings.get(subtype.key);
          if (subtypeSettings) {
            subtypeSettings.show = show;
          }
        }
      }
      this._renderFilterUI();
      this.callbacks.onFilterChange?.({ type: 'show', categoryKey, value: show });
    }
  }

  /**
   * Toggle verify setting for a category
   */
  setCategoryVerify(categoryKey, verify) {
    const category = this.categorySettings.get(categoryKey);
    if (category) {
      category.verify = verify;
      // Also update all subtypes
      const categoryDef = CATEGORY_DEFINITIONS[categoryKey];
      if (categoryDef) {
        for (const subtype of categoryDef.subtypes) {
          const subtypeSettings = this.subtypeSettings.get(subtype.key);
          if (subtypeSettings) {
            subtypeSettings.verify = verify;
          }
        }
      }
      this._renderFilterUI();
      this.callbacks.onFilterChange?.({ type: 'verify', categoryKey, value: verify });
    }
  }

  /**
   * Toggle show setting for a subtype
   */
  setSubtypeShow(typeKey, show) {
    const subtype = this.subtypeSettings.get(typeKey);
    if (subtype) {
      subtype.show = show;
      this.callbacks.onFilterChange?.({ type: 'show', typeKey, value: show });
    }
  }

  /**
   * Toggle verify setting for a subtype
   */
  setSubtypeVerify(typeKey, verify) {
    const subtype = this.subtypeSettings.get(typeKey);
    if (subtype) {
      subtype.verify = verify;
      this.callbacks.onFilterChange?.({ type: 'verify', typeKey, value: verify });
    }
  }

  /**
   * Toggle category expansion
   */
  toggleCategoryExpanded(categoryKey) {
    const category = this.categorySettings.get(categoryKey);
    if (category) {
      category.expanded = !category.expanded;
      this._renderFilterUI();
    }
  }

  /**
   * Mount the filter UI into a container element
   */
  mount(container) {
    this.containerEl = container;
    this._renderFilterUI();
  }

  /**
   * Force re-render of the filter UI
   */
  forceRender() {
    if (this.containerEl) {
      this._renderFilterUI();
    }
  }

  /**
   * Update the count display for a subtype
   */
  _updateSubtypeCount(typeKey, count) {
    if (!this.containerEl) return;
    
    const countEl = this.containerEl.querySelector(
      `.subtype-row[data-type="${CSS.escape(typeKey)}"] .type-count`
    );
    if (countEl) {
      countEl.textContent = count > 0 ? `(${count})` : '';
      countEl.classList.toggle('has-count', count > 0);
    }
  }

  /**
   * Update the count display for a category
   */
  _updateCategoryCount(categoryKey, count) {
    if (!this.containerEl) return;
    
    const countEl = this.containerEl.querySelector(
      `.category-row[data-category="${CSS.escape(categoryKey)}"] .category-count`
    );
    if (countEl) {
      countEl.textContent = count > 0 ? `(${count})` : '';
      countEl.classList.toggle('has-count', count > 0);
    }
  }

  /**
   * Render the filter UI
   */
  _renderFilterUI() {
    if (!this.containerEl) return;

    this.containerEl.innerHTML = '';

    // Create header
    const header = document.createElement('div');
    header.className = 'type-filter-header';
    header.innerHTML = `
      <span class="type-filter-title">Citation Types</span>
      <div class="type-filter-legend">
        <span class="legend-item" title="Show/hide in list">Show</span>
        <span class="legend-item" title="Include in verification">Verify</span>
      </div>
    `;
    this.containerEl.appendChild(header);

    // Render each category
    for (const [categoryKey, categoryDef] of Object.entries(CATEGORY_DEFINITIONS)) {
      const categorySettings = this.categorySettings.get(categoryKey);
      const categoryEl = this._createCategoryRow(categoryKey, categoryDef, categorySettings);
      this.containerEl.appendChild(categoryEl);
    }
  }

  /**
   * Create a category row with expandable subtypes
   */
  _createCategoryRow(categoryKey, categoryDef, settings) {
    const wrapper = document.createElement('div');
    wrapper.className = 'category-wrapper';
    wrapper.dataset.category = categoryKey;

    // Category header row
    const row = document.createElement('div');
    row.className = 'category-row';
    row.dataset.category = categoryKey;

    // Expand/collapse toggle
    const toggle = document.createElement('button');
    toggle.className = 'category-toggle';
    toggle.innerHTML = settings.expanded ? '▼' : '▶';
    toggle.title = settings.expanded ? 'Collapse' : 'Expand';
    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleCategoryExpanded(categoryKey);
    });
    row.appendChild(toggle);

    // Category label with count
    const label = document.createElement('span');
    label.className = 'category-label';
    const countClass = settings.totalCount > 0 ? 'has-count' : '';
    label.innerHTML = `
      <span class="category-name">${categoryDef.label}</span>
      <span class="category-count ${countClass}">${settings.totalCount > 0 ? `(${settings.totalCount})` : ''}</span>
    `;
    row.appendChild(label);

    // Controls
    const controls = document.createElement('div');
    controls.className = 'category-controls';

    // Show checkbox
    const showLabel = document.createElement('label');
    showLabel.className = 'filter-checkbox-label';
    showLabel.title = 'Show/hide all citations of this type';
    const showCheckbox = document.createElement('input');
    showCheckbox.type = 'checkbox';
    showCheckbox.checked = settings.show;
    showCheckbox.className = 'filter-checkbox show-checkbox';
    showCheckbox.addEventListener('change', (e) => {
      e.stopPropagation();
      this.setCategoryShow(categoryKey, showCheckbox.checked);
    });
    showLabel.appendChild(showCheckbox);
    controls.appendChild(showLabel);

    // Verify checkbox
    const verifyLabel = document.createElement('label');
    verifyLabel.className = 'filter-checkbox-label';
    verifyLabel.title = 'Include all citations of this type in verification';
    const verifyCheckbox = document.createElement('input');
    verifyCheckbox.type = 'checkbox';
    verifyCheckbox.checked = settings.verify;
    verifyCheckbox.className = 'filter-checkbox verify-checkbox';
    verifyCheckbox.addEventListener('change', (e) => {
      e.stopPropagation();
      this.setCategoryVerify(categoryKey, verifyCheckbox.checked);
    });
    verifyLabel.appendChild(verifyCheckbox);
    controls.appendChild(verifyLabel);

    row.appendChild(controls);
    wrapper.appendChild(row);

    // Subtypes container (collapsible)
    const subtypesContainer = document.createElement('div');
    subtypesContainer.className = 'subtypes-container';
    subtypesContainer.style.display = settings.expanded ? 'block' : 'none';

    for (const subtype of categoryDef.subtypes) {
      const subtypeSettings = this.subtypeSettings.get(subtype.key);
      const subtypeRow = this._createSubtypeRow(subtype, subtypeSettings, settings);
      subtypesContainer.appendChild(subtypeRow);
    }

    wrapper.appendChild(subtypesContainer);
    return wrapper;
  }

  /**
   * Create a subtype row
   */
  _createSubtypeRow(subtype, settings, categorySettings) {
    const row = document.createElement('div');
    row.className = 'subtype-row';
    row.dataset.type = subtype.key;

    // Indent spacer
    const spacer = document.createElement('span');
    spacer.className = 'subtype-spacer';
    row.appendChild(spacer);

    // Label with count
    const label = document.createElement('span');
    label.className = 'type-label';
    const countClass = settings.count > 0 ? 'has-count' : '';
    label.innerHTML = `
      <span class="type-name">${subtype.label}</span>
      <span class="type-count ${countClass}">${settings.count > 0 ? `(${settings.count})` : ''}</span>
    `;
    row.appendChild(label);

    // Controls
    const controls = document.createElement('div');
    controls.className = 'type-filter-controls';

    // Show checkbox
    const showLabel = document.createElement('label');
    showLabel.className = 'filter-checkbox-label';
    showLabel.title = 'Show/hide this citation subtype';
    const showCheckbox = document.createElement('input');
    showCheckbox.type = 'checkbox';
    showCheckbox.checked = settings.show;
    showCheckbox.className = 'filter-checkbox show-checkbox';
    showCheckbox.disabled = !categorySettings.show; // Disabled if category is hidden
    showCheckbox.addEventListener('change', () => {
      this.setSubtypeShow(subtype.key, showCheckbox.checked);
    });
    showLabel.appendChild(showCheckbox);
    controls.appendChild(showLabel);

    // Verify checkbox
    const verifyLabel = document.createElement('label');
    verifyLabel.className = 'filter-checkbox-label';
    verifyLabel.title = 'Include this subtype in verification';
    const verifyCheckbox = document.createElement('input');
    verifyCheckbox.type = 'checkbox';
    verifyCheckbox.checked = settings.verify;
    verifyCheckbox.className = 'filter-checkbox verify-checkbox';
    verifyCheckbox.disabled = !categorySettings.verify; // Disabled if category is not verified
    verifyCheckbox.addEventListener('change', () => {
      this.setSubtypeVerify(subtype.key, verifyCheckbox.checked);
    });
    verifyLabel.appendChild(verifyCheckbox);
    controls.appendChild(verifyLabel);

    row.appendChild(controls);
    return row;
  }
}

// Singleton instance
let filterManagerInstance = null;

/**
 * Get or create the citation type filter manager singleton
 */
export function getCitationTypeFilterManager() {
  if (!filterManagerInstance) {
    filterManagerInstance = new CitationTypeFilterManager();
  }
  return filterManagerInstance;
}

/**
 * Ensure the citation type filter panel exists in the DOM
 */
export function ensureCitationTypeFilterPanel(containerId = 'fn-pane') {
  const container = document.getElementById(containerId) || document.querySelector(`#${containerId}`);
  if (!container) {
    console.warn(`[CitationTypeFilter] Container ${containerId} not found`);
    return null;
  }

  let panel = container.querySelector('.citation-type-filter-panel');
  if (panel) {
    return panel.querySelector('.citation-type-filter-content');
  }

  // Create the collapsible panel
  panel = document.createElement('div');
  panel.className = 'citation-type-filter-panel';
  
  // Create toggle header
  const toggleHeader = document.createElement('button');
  toggleHeader.className = 'citation-type-filter-toggle';
  toggleHeader.setAttribute('aria-expanded', 'false');
  toggleHeader.innerHTML = `
    <span class="toggle-icon">›</span>
    <span class="toggle-text">Filter by Citation Type</span>
    <span class="filter-summary"></span>
  `;

  // Create content container
  const content = document.createElement('div');
  content.className = 'citation-type-filter-content';
  
  // Toggle behavior
  toggleHeader.addEventListener('click', () => {
    const expanded = toggleHeader.getAttribute('aria-expanded') === 'true';
    toggleHeader.setAttribute('aria-expanded', String(!expanded));
    content.classList.toggle('show', !expanded);
    toggleHeader.querySelector('.toggle-icon').classList.toggle('expanded', !expanded);
  });

  panel.appendChild(toggleHeader);
  panel.appendChild(content);

  // Insert after the pane header or at the start
  const paneHeader = container.querySelector('.pane-header');
  if (paneHeader && paneHeader.nextSibling) {
    container.insertBefore(panel, paneHeader.nextSibling);
  } else if (paneHeader) {
    paneHeader.after(panel);
  } else if (container.firstChild) {
    container.insertBefore(panel, container.firstChild);
  } else {
    container.appendChild(panel);
  }

  // Mount the filter manager
  const manager = getCitationTypeFilterManager();
  manager.mount(content);
  
  // Chain callbacks to also update summary
  const existingOnFilterChange = manager.callbacks.onFilterChange;
  const existingOnTypesUpdated = manager.callbacks.onTypesUpdated;
  
  manager.setCallbacks({
    onFilterChange: (change) => {
      existingOnFilterChange?.(change);
      updateFilterSummary(panel);
    },
    onTypesUpdated: (stats) => {
      existingOnTypesUpdated?.(stats);
      updateFilterSummary(panel);
    }
  });

  return content;
}

/**
 * Update the filter summary shown in the toggle header
 */
function updateFilterSummary(panel) {
  const manager = getCitationTypeFilterManager();
  const stats = manager.getTypeStats();
  const summaryEl = panel.querySelector('.filter-summary');
  
  if (!summaryEl) return;
  
  const hiddenCount = stats.totalCount - stats.visibleCount;
  const noVerifyCount = stats.totalCount - stats.verifyCount;
  
  const parts = [];
  if (stats.totalCount > 0) {
    parts.push(`${stats.totalCount} citations`);
  }
  if (hiddenCount > 0) {
    parts.push(`${hiddenCount} hidden`);
  }
  if (noVerifyCount > 0) {
    parts.push(`${noVerifyCount} excluded`);
  }
  
  summaryEl.textContent = parts.length > 0 ? `(${parts.join(', ')})` : '';
}

// Export for use in other modules
export { CATEGORY_DEFINITIONS };
